import { FC, useMemo } from 'react';

import {
  GiftDeliveryForStore,
  GiftDeliveryShareStatus,
  GiftDeliveryStatus,
} from '@shared/api/giftDelivery/giftDelivery.interface';
import { fileBucketUrl } from '@shared/utils/fileUtils';

import {
  getDeliveryShareStatusLabel,
  getDeliveryShareStatusStyle,
} from '../utils/giftOrderCoupon.utils';

interface IndividualDeliveryRowProps {
  delivery: GiftDeliveryForStore;
  onShare: (delivery: GiftDeliveryForStore) => void;
  onMemo: (delivery: GiftDeliveryForStore) => void;
  isSharing: boolean;
}

const formatConfirmedAt = (date: Date | string) => {
  const d = new Date(date);
  return `${d.getDate()}일 ${String(d.getHours()).padStart(2, '0')}:${String(
    d.getMinutes(),
  ).padStart(2, '0')} 완료`;
};

export const IndividualDeliveryRow: FC<IndividualDeliveryRowProps> = ({
  delivery,
  onShare,
  onMemo,
  isSharing,
}) => {
  const giftItem = delivery.giftItem;
  const imageUrl = useMemo(() => {
    if (typeof giftItem === 'object') {
      return giftItem?.giftItemInfo?.image?.url || giftItem?.image?.url || '';
    }
    return '';
  }, [giftItem]);

  const itemName = typeof giftItem === 'object' ? giftItem?.name || '' : '';
  const isShared = delivery.shareStatus !== GiftDeliveryShareStatus.NotShared;
  const isAddressSubmitted =
    delivery.deliveryStatus !== GiftDeliveryStatus.WaitingAddress;

  return (
    <div
      className={`flex items-center gap-3 rounded-lg bg-white p-3 shadow-sm ${
        isShared ? 'ring-1 ring-green-400' : ''
      }`}
    >
      <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
        {imageUrl ? (
          <img
            src={fileBucketUrl(imageUrl, 'w200')}
            alt={itemName}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-[10px] text-gray-400">
            No Image
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">#{delivery.seq}</span>
          <span
            className={`break-keep rounded px-1.5 py-0.5 text-[10px] font-medium ${getDeliveryShareStatusStyle(
              delivery.shareStatus,
            )}`}
          >
            {getDeliveryShareStatusLabel(delivery.shareStatus)}
          </span>
        </div>
        <p className="mt-0.5 truncate text-sm font-medium text-gray-800">
          {itemName}
          {delivery.quantity > 1 && (
            <span className="ml-1 text-xs text-gray-400">x{delivery.quantity}</span>
          )}
        </p>
      </div>

      {isShared && delivery.confirmedAt ? (
        <div className="flex flex-shrink-0 flex-col items-end gap-0.5">
          {delivery.memo ? (
            <span className="truncate rounded-lg bg-purple-50 px-3 py-1 text-xs font-medium text-purple-600">
              {delivery.memo}
            </span>
          ) : (
            <button
              onClick={() => onMemo(delivery)}
              className="rounded-lg border border-dashed border-purple-300 px-3 py-1 text-xs text-purple-400 active:bg-purple-50"
            >
              메모 남기기
            </button>
          )}
          <span className="text-[10px] text-gray-400">
            {formatConfirmedAt(delivery.confirmedAt)}
          </span>
        </div>
      ) : (
        !isAddressSubmitted && (
          <button
            onClick={() => onShare(delivery)}
            disabled={isSharing}
            className={`flex-shrink-0 rounded-xl px-5 py-2.5 text-sm font-bold transition ${
              isShared
                ? 'bg-green-100 text-green-700'
                : 'bg-purple-600 text-white active:bg-purple-700'
            } ${isSharing ? 'opacity-50' : ''}`}
          >
            {isSharing ? '...' : isShared ? '다시 전달' : '전달하기'}
          </button>
        )
      )}
    </div>
  );
};
